
import { sendContactForm } from '@/utils/contactFormService';
import { toast } from "sonner";
import { getScoreInterpretation } from './useTelemarketingScan';
import { incrementScanCounter } from './useScanCounter';
import { z } from 'zod';
import { validateHoneypot } from '@/components/ui/honeypot-field';

// Admin email to receive results
const ADMIN_EMAIL = "info@afsprakenmaker.be";

// Form schema for contact information - define it here to avoid circular dependency
const formSchema = z.object({
  name: z.string().min(2),
  company: z.string().min(2),
  email: z.string().email(),
  website: z.string().optional(), // Honeypot field
});

// Form data type
export type ScanFormData = z.infer<typeof formSchema>;

// Function to send scan results to admin
export const sendScanResultsToAdmin = async (data: ScanFormData, score: {
  totalScore: number;
  maxScore: number;
  scorePercentage: number;
}): Promise<boolean> => {
  try {
    // Check if this might be a spam submission
    if (!validateHoneypot(data)) {
      console.log("Potential spam submission detected and blocked");
      // Return true to avoid showing error to potential spammer
      return true;
    }

    const interpretation = getScoreInterpretation(score.scorePercentage);
    
    // Prepare additional info with all the relevant scan details
    const additionalInfo = {
      'Score': `${score.totalScore} van ${score.maxScore} punten (${score.scorePercentage}%)`,
      'Interpretatie': interpretation.title,
      'Beschrijving': interpretation.description,
      'Aanbevelingen': interpretation.recommendations.join(', ')
    };
    
    // Create a clean data object without the honeypot field
    const cleanData = {
      name: data.name,
      company: data.company,
      email: data.email
    };
    
    // Use the global contact form service and return its success property
    const result = await sendContactForm(
      cleanData,
      `Nieuwe Telemarketing Scan Resultaten - ${data.company}`,
      additionalInfo,
      'bert@afsprakenmaker.be'
    );
    
    return result.success;
    
  } catch (error) {
    console.error("Error sending admin notification:", error);
    // Don't show errors to the user, just log them
    return false;
  }
};

// Hook to handle form submission
export const useScanResultSubmission = (
  totalScore: number,
  maxScore: number,
  onSuccess: () => void
) => {
  const submitResults = async (data: ScanFormData) => {
    // If honeypot is filled, silently succeed but don't process
    if (!validateHoneypot(data)) {
      console.log("Honeypot triggered - spam submission detected");
      // Still show success to avoid revealing the honeypot
      toast.success("Bedankt! Uw scanresultaten zijn verzonden.", {
        description: "U ontvangt uw resultaten direct op deze pagina.",
      });
      onSuccess();
      return;
    }
    
    // Calculate percentage score
    const scorePercentage = Math.round((totalScore / maxScore) * 100);
    
    // Send email to admin with the results using contactFormService
    const success = await sendScanResultsToAdmin(data, {
      totalScore,
      maxScore,
      scorePercentage
    });
    
    if (success) {
      // Track successful scan completion as conversion
      const { trackConversion } = await import('@/utils/analyticsService');
      trackConversion();
      
      toast.success("Bedankt! Uw scanresultaten zijn verzonden.", {
        description: "U ontvangt uw resultaten direct op deze pagina.",
      });
      
      onSuccess();
    } else {
      // If the submission failed, increment the local counter as a fallback
      incrementScanCounter();
      
      toast.error("Er is een fout opgetreden bij het versturen van uw resultaten.", {
        description: "Probeer het later nog eens of neem contact met ons op.",
      });
    }
  };
  
  return { submitResults };
};
